import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { concatMap, EMPTY, filter, map, of, switchMap, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { SurveyActionsFacade, SurveyService } from '@ps/survey-data-access';
import { SURVEY_STATUS } from '@ps/survey-kernel';
import { Undefinable } from '@ps/utils';
import {
  BasicToken,
  BASIC_TOKEN_SAVED_MESSAGE,
  BILLING_CODE_ALREADY_USED_WARNING,
  INITIAL_SURVEY_FILTERS,
  SurveyUrlsFormMapper,
} from '@ps/survey-utils';

import { UniqueBuyerEntryLinksService } from '../../services/unique-buyer-entry-links/unique-buyer-entry-links-service';
import { AidaService } from '../../services/aida/aida.service';

import { selectSurvey } from '../core/survey-creation-core.selectors';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import { SurveyCreationAudienceApiActions } from '../audience/survey-creation-audience.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

@Injectable()
export class SurveyCreationLaunchEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _surveyActions = inject(SurveyActionsFacade);
  private readonly _surveyService = inject(SurveyService);
  private readonly _toast = inject(ToastService);
  private readonly _uniqueBuyerEntryLinksService = inject(
    UniqueBuyerEntryLinksService,
  );

  private readonly _aidaService = inject(AidaService);

  private readonly _surveyUrlsFormMapper = new SurveyUrlsFormMapper();

  // Keeps surveyUrls in sync with the loaded/created survey whether or not the
  // Launch page was ever mounted. The other 8 sections are only ever populated
  // by SurveyLaunchComponent itself, once mounted.
  public readonly populateSurveyUrlsOnSurveyLoaded$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.cloneSurveySuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
      ),
      map(({ survey }) =>
        SurveyCreationLaunchActions.patchSurveyUrls({
          surveyUrls: this._surveyUrlsFormMapper.fromSurvey(survey),
        }),
      ),
    ),
  );

  public readonly loadProjectManagers$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.init),
        tap(() => {
          if (
            this._authSelectors.isBuyer() ||
            this._authSelectors.hasImpersonatedBuyer()
          ) {
            this._surveyActions.loadBuyerManagers(
              this._authSelectors.activeCompanyId(),
            );
          }
          if (this._authSelectors.isOperator()) {
            this._surveyActions.loadOperatorManagers();
          }
        }),
      ),
    { dispatch: false },
  );

  public readonly loadBuyerSettingsForSurvey$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
        tap(({ survey }) =>
          this._surveyActions.loadBuyerSettings(survey.buyerCompanyId),
        ),
      ),
    { dispatch: false },
  );

  public readonly downloadEntryLinksTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.downloadEntryLinksTemplate),
        switchMap(() =>
          this._uniqueBuyerEntryLinksService
            .downloadTemplateFile()
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  // Fires once per focusout, matching legacy's imperative on-blur check —
  // the component only forwards the DOM event as an action; the API call
  // and self-exclusion logic stay in this Effect.
  public readonly checkBillingCodeAlreadyUsed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationLaunchActions.checkBillingCodeAlreadyUsed),
        filter(({ billingId }) => !!billingId),
        concatLatestFrom(() => this._store.select(selectSurvey)),
        switchMap(([{ billingId }, survey]) =>
          this._surveyService
            .getAll({
              filters: {
                ...INITIAL_SURVEY_FILTERS,
                status: SURVEY_STATUS.INVOICED,
                billing: billingId,
                buyerId: survey?.buyerCompanyId || 0,
              },
              context: { targeting: 0 },
            })
            .pipe(
              tap((invoicedSurveys) => {
                const usedByAnotherSurvey = invoicedSurveys.some(
                  (invoicedSurvey) =>
                    invoicedSurvey.billingId !== survey?.billingId,
                );
                if (usedByAnotherSurvey) {
                  this._toast.warning(BILLING_CODE_ALREADY_USED_WARNING, {
                    keepOpen: false,
                  });
                }
              }),
              this.psCatchError(() => EMPTY, { notify: true }),
            ),
        ),
      ),
    { dispatch: false },
  );
  // switchMap: only the latest survey's verdict matters.
  public readonly loadAidaEligibility$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.loadAidaEligibility),
      switchMap(({ surveyId }) =>
        this._aidaService.eligibilityCheck(surveyId).pipe(
          map((eligibility) =>
            SurveyCreationLaunchApiActions.aidaEligibilityLoaded({
              eligibility,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationLaunchApiActions.aidaEligibilityFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // The patched state is written back through one action. A failure reports the
  // server's message and re-emits the last saved token, so the toggle the user
  // just flipped reverts (legacy left it stuck when the token was empty).
  public readonly updateBasicToken$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.updateBasicToken),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      // Sequential, never cancelled: an aborted request may already be applied
      // server-side, which would leave the store behind the server's token.
      concatMap(([{ enable, value }, survey]) =>
        Option.from(survey).match({
          some: ({ id, basicToken }) => {
            const savedBasicToken: Undefinable<BasicToken> = basicToken;
            return this._surveyService
              .updateBasicToken(id, { enable, value })
              .pipe(
                tap(() => this._toast.success(BASIC_TOKEN_SAVED_MESSAGE)),
                map((patchedBasicToken) =>
                  SurveyCreationLaunchApiActions.basicTokenPatched({
                    basicToken: patchedBasicToken,
                  }),
                ),
                this.psCatchError(
                  () =>
                    of(
                      SurveyCreationLaunchApiActions.basicTokenPatched({
                        basicToken: savedBasicToken ?? {
                          enable: false,
                          value: '',
                        },
                      }),
                    ),
                  { notify: true },
                ),
              );
          },
          none: () => EMPTY,
        }),
      ),
    ),
  );
}
